import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { giaoVienAPI, gvConstraintAPI } from '../services/api';
import { toast } from 'react-toastify';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import ImportExcel from '../components/ImportExcel';
import { FiPlus, FiEdit2, FiTrash2, FiUserCheck, FiMail, FiPhone, FiUpload, FiDownload, FiChevronUp, FiChevronDown, FiSliders } from 'react-icons/fi';
import * as XLSX from 'xlsx';

// Lưới yêu cầu thời gian GV
const THU_LIST = [
  { value: 2, label: 'Thứ 2' }, { value: 3, label: 'Thứ 3' },
  { value: 4, label: 'Thứ 4' }, { value: 5, label: 'Thứ 5' },
  { value: 6, label: 'Thứ 6' }, { value: 7, label: 'Thứ 7' },
];
const BUOI_LIST = ['Sang', 'Chieu'];
const TIET_LIST = [1, 2, 3, 4, 5];
const LOAI_ORDER = [null, 'must_teach', 'preferred_avoid', 'blocked'];
const LOAI_STYLE = {
  null:             { bg: '#f8f9fa', border: '#dee2e6', label: 'Bình thường' },
  must_teach:       { bg: '#d1e7dd', border: '#198754', label: 'Phải dạy ✅' },
  preferred_avoid:  { bg: '#fff3cd', border: '#ffc107', label: 'Tránh nếu được ⚠️' },
  blocked:          { bg: '#f8d7da', border: '#dc3545', label: 'Không thể dạy 🚫' },
};

function GvConstraintGrid({ gvId, tenGv, onClose }) {
  // grid[thu][buoi][tiet] = loai | null
  const [grid, setGrid] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!gvId) return;
    gvConstraintAPI.getByGiaoVien(gvId).then(res => {
      const rows = res.data?.data || [];
      const g = {};
      for (const thu of THU_LIST.map(t => t.value)) {
        g[thu] = {};
        for (const buoi of BUOI_LIST) {
          g[thu][buoi] = {};
          for (const tiet of TIET_LIST) g[thu][buoi][tiet] = null;
        }
      }
      for (const r of rows) {
        // Expand: nếu thu/buoi/tiet là null → áp cho tất cả
        const thuArr = r.thu ? [r.thu] : THU_LIST.map(t => t.value);
        const buoiArr = r.buoi ? [r.buoi] : BUOI_LIST;
        const tietArr = r.tiet ? [r.tiet] : TIET_LIST;
        for (const th of thuArr)
          for (const b of buoiArr)
            for (const ti of tietArr)
              if (g[th]?.[b]) g[th][b][ti] = r.loai;
      }
      setGrid(g);
    }).catch(() => {});
  }, [gvId]);

  const toggle = useCallback((thu, buoi, tiet) => {
    setGrid(prev => {
      const cur = prev[thu]?.[buoi]?.[tiet] ?? null;
      const idx = LOAI_ORDER.indexOf(cur);
      const next = LOAI_ORDER[(idx + 1) % LOAI_ORDER.length];
      return {
        ...prev,
        [thu]: { ...prev[thu], [buoi]: { ...prev[thu][buoi], [tiet]: next } }
      };
    });
  }, []);

  const handleSave = async (force = false) => {
    setSaving(true);
    try {
      const constraints = [];
      for (const thu of THU_LIST.map(t => t.value))
        for (const buoi of BUOI_LIST)
          for (const tiet of TIET_LIST) {
            const loai = grid[thu]?.[buoi]?.[tiet];
            if (loai) constraints.push({ thu, buoi, tiet, loai });
          }

      let res;
      try {
        res = await gvConstraintAPI.saveForGiaoVien(gvId, constraints, force);
      } catch (err) {
        const payload = err.response?.data;
        if (!force && err.response?.status === 422 && payload?.data?.needConfirm) {
          const errors = payload.data.errors || [];
          const warnings = payload.data.warnings || [];
          const lines = [
            ...errors.map(e => `• ${e.message}`),
            ...warnings.map(w => `• ${w.message}`)
          ].slice(0, 8);
          const ok = window.confirm(
            `Yêu cầu có vấn đề:\n\n${lines.join('\n')}\n\nVẫn lưu yêu cầu này?`
          );
          if (!ok) return;
          res = await gvConstraintAPI.saveForGiaoVien(gvId, constraints, true);
        } else {
          toast.error(payload?.message || 'Lưu thất bại!');
          return;
        }
      }

      const warnings = res.data?.data?.warnings || [];
      const errors = res.data?.data?.errors || [];
      errors.slice(0, 3).forEach(e => toast.error(e.message, { autoClose: 9000 }));
      warnings.slice(0, 5).forEach(w => toast.warning(w.message, { autoClose: 8000 }));
      toast.success(res.data?.message || 'Đã lưu yêu cầu thời gian!');
      onClose();
    } catch {
      toast.error('Lưu thất bại!');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = () => {
    setGrid(prev => {
      const g = {};
      for (const thu of THU_LIST.map(t => t.value)) {
        g[thu] = {};
        for (const buoi of BUOI_LIST) {
          g[thu][buoi] = {};
          for (const tiet of TIET_LIST) g[thu][buoi][tiet] = null;
        }
      }
      return g;
    });
  };

  return (
    <div>
      <p style={{ marginBottom: 10, color: '#666', fontSize: 13 }}>
        Click vào ô để chuyển trạng thái:
        <span style={{ marginLeft: 8, padding: '2px 8px', background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: 4 }}>Bình thường</span>
        {' → '}
        <span style={{ padding: '2px 8px', background: '#d1e7dd', border: '1px solid #198754', borderRadius: 4 }}>✅ Phải dạy</span>
        {' → '}
        <span style={{ padding: '2px 8px', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 4 }}>⚠️ Tránh nếu được</span>
        {' → '}
        <span style={{ padding: '2px 8px', background: '#f8d7da', border: '1px solid #dc3545', borderRadius: 4 }}>🚫 Không thể dạy</span>
      </p>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
          <thead>
            <tr>
              <th style={thStyle}>Buổi</th>
              <th style={thStyle}>Tiết</th>
              {THU_LIST.map(t => (
                <th key={t.value} style={{ ...thStyle, minWidth: 72 }}>{t.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {BUOI_LIST.map(buoi => (
              TIET_LIST.map((tiet, tietIdx) => (
                <tr key={`${buoi}-${tiet}`}>
                  {tietIdx === 0 && (
                    <td rowSpan={TIET_LIST.length} style={{ ...tdStyle, fontWeight: 600, textAlign: 'center', background: buoi === 'Sang' ? '#e3f2fd' : '#fff8e1' }}>
                      {buoi === 'Sang' ? 'Sáng' : 'Chiều'}
                    </td>
                  )}
                  <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 500 }}>Tiết {tiet}</td>
                  {THU_LIST.map(({ value: thu }) => {
                    const loai = grid[thu]?.[buoi]?.[tiet] ?? null;
                    const style = LOAI_STYLE[loai];
                    return (
                      <td key={thu}
                        onClick={() => toggle(thu, buoi, tiet)}
                        title={style.label}
                        style={{
                          ...tdStyle, cursor: 'pointer', textAlign: 'center',
                          background: style.bg, border: `2px solid ${style.border}`,
                          userSelect: 'none', fontSize: 16, lineHeight: '32px', height: 36,
                          transition: 'background 0.15s',
                        }}
                      >
                        {loai === 'blocked' ? '🚫' : loai === 'preferred_avoid' ? '⚠️' : loai === 'must_teach' ? '✅' : ''}
                      </td>
                    );
                  })}
                </tr>
              ))
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
        <button className="btn btn-secondary" onClick={handleClear} disabled={saving}>Xóa tất cả</button>
        <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Hủy</button>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Đang lưu...' : 'Lưu yêu cầu'}
        </button>
      </div>
    </div>
  );
}

const thStyle = { padding: '6px 8px', background: '#f1f3f5', border: '1px solid #dee2e6', fontWeight: 600 };
const tdStyle = { padding: '4px 6px', border: '1px solid #dee2e6' };

const GiaoVienPage = () => {
  const { t } = useTranslation();
  const [giaoViens, setGiaoViens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [constraintGV, setConstraintGV] = useState(null); // GV đang mở modal yêu cầu
  const [selectedGV, setSelectedGV] = useState(null);
  const [formData, setFormData] = useState({
    tenGv: '',
    chuyenMon: '',
    email: '',
    dienThoai: ''
  });

  useEffect(() => {
    fetchGiaoViens();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchGiaoViens = async () => {
    try {
      const response = await giaoVienAPI.getAll();
      if (response.data.success) {
        setGiaoViens(response.data.data);
      }
    } catch (error) {
      toast.error(t('gv.cannotLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedGV) {
        await giaoVienAPI.update(selectedGV.gv_id, formData);
        toast.success(t('gv.updateSuccess'));
      } else {
        await giaoVienAPI.create(formData);
        toast.success(t('gv.addSuccess'));
      }
      setModalOpen(false);
      setSelectedGV(null);
      setFormData({ tenGv: '', chuyenMon: '', email: '', dienThoai: '' });
      fetchGiaoViens();
    } catch (error) {
      toast.error(error.response?.data?.message || t('common.errorOccurred'));
    }
  };

  const handleEdit = (gv) => {
    setSelectedGV(gv);
    setFormData({
      tenGv: gv.ten_gv,
      chuyenMon: gv.chuyen_mon || '',
      email: gv.email || '',
      dienThoai: gv.dien_thoai || ''
    });
    setModalOpen(true);
  };

  const handleDelete = (gv) => {
    setSelectedGV(gv);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await giaoVienAPI.delete(selectedGV.gv_id);
      toast.success(t('gv.deleteSuccess'));
      setConfirmOpen(false);
      setSelectedGV(null);
      fetchGiaoViens();
    } catch (error) {
      toast.error(error.response?.data?.message || t('gv.cannotDelete'));
    }
  };

  const openAddModal = () => {
    setSelectedGV(null);
    setFormData({ tenGv: '', chuyenMon: '', email: '', dienThoai: '' });
    setModalOpen(true);
  };

  const handleExportExcel = () => {
    const header = ['Tên giáo viên', 'Chuyên môn', 'Email', 'Điện thoại'];
    const rows = giaoViens.map(gv => [
      gv.ten_gv,
      gv.chuyen_mon || '',
      gv.email || '',
      gv.dien_thoai || ''
    ]);
    const ws = XLSX.utils.aoa_to_sheet([header, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Giáo viên');
    XLSX.writeFile(wb, 'giao_vien.xlsx');
  };

  const handleImport = async (file) => {
    const response = await giaoVienAPI.import(file);
    if (response.data.success) {
      fetchGiaoViens();
      toast.success(response.data.message);
    }
    return response;
  };

  // Sắp xếp thứ tự
  const moveUp = async (giaoVien, index) => {
    if (index === 0) return; // Đã ở vị trí đầu

    try {
      // Lấy thứ tự của item phía trên
      const prevGV = giaoViens[index - 1];
      const newOrder = prevGV.thu_tu || index;

      // Cập nhật thứ tự cho item hiện tại
      await giaoVienAPI.updateOrder(giaoVien.gv_id, newOrder);

      fetchGiaoViens();
      toast.success(t('common.orderUpdated'));
    } catch (error) {
      console.error('Error moving up:', error);
      toast.error(t('common.cannotUpdateOrder'));
    }
  };

  const moveDown = async (giaoVien, index) => {
    if (index === giaoViens.length - 1) return; // Đã ở vị trí cuối

    try {
      // Lấy thứ tự của item phía dưới
      const nextGV = giaoViens[index + 1];
      const newOrder = nextGV.thu_tu || (index + 2);

      // Cập nhật thứ tự cho item hiện tại
      await giaoVienAPI.updateOrder(giaoVien.gv_id, newOrder);

      fetchGiaoViens();
      toast.success(t('common.orderUpdated'));
    } catch (error) {
      console.error('Error moving down:', error);
      toast.error(t('common.cannotUpdateOrder'));
    }
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div>
      <div className="card">
        <div className="card-header">
          <h2>{t('gv.pageTitle')} ({giaoViens.length})</h2>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="btn btn-secondary" onClick={handleExportExcel}>
              <FiDownload /> Export Excel
            </button>
            <button className="btn btn-secondary" onClick={() => setImportOpen(true)}>
              <FiUpload /> {t('gv.importBtn')}
            </button>
            <button className="btn btn-primary" onClick={openAddModal}>
              <FiPlus /> {t('gv.addBtn')}
            </button>
          </div>
        </div>
        <div className="card-body">
          {giaoViens.length === 0 ? (
            <div className="empty-state">
              <FiUserCheck />
              <h3>{t('gv.empty')}</h3>
              <p>{t('gv.emptyHint')}</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('gv.colOrder')}</th>
                    <th>{t('gv.colNo')}</th>
                    <th>{t('gv.colName')}</th>
                    <th>{t('gv.colSpecialty')}</th>
                    <th>{t('gv.colContact')}</th>
                    <th>{t('gv.colActions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {giaoViens.map((gv, index) => (
                    <tr key={gv.gv_id}>
                      <td>
                        <div className="order-controls">
                          <button
                            className="btn btn-secondary btn-icon btn-sm"
                            onClick={() => moveUp(gv, index)}
                            disabled={index === 0}
                            title={t('common.moveUp')}
                          >
                            <FiChevronUp />
                          </button>
                          <button
                            className="btn btn-secondary btn-icon btn-sm"
                            onClick={() => moveDown(gv, index)}
                            disabled={index === giaoViens.length - 1}
                            title={t('common.moveDown')}
                          >
                            <FiChevronDown />
                          </button>
                        </div>
                      </td>
                      <td>{index + 1}</td>
                      <td><strong>{gv.ten_gv}</strong></td>
                      <td>{gv.chuyen_mon || '-'}</td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {gv.email && (
                            <span style={{ fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <FiMail size={12} /> {gv.email}
                            </span>
                          )}
                          {gv.dien_thoai && (
                            <span style={{ fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <FiPhone size={12} /> {gv.dien_thoai}
                            </span>
                          )}
                          {!gv.email && !gv.dien_thoai && '-'}
                        </div>
                      </td>
                      <td>
                        <div className="table-actions">
                          <button
                            className="btn btn-secondary btn-icon"
                            onClick={() => setConstraintGV(gv)}
                            title="Yêu cầu thời gian dạy"
                          >
                            <FiSliders />
                          </button>
                          <button
                            className="btn btn-secondary btn-icon"
                            onClick={() => handleEdit(gv)}
                            title={t('common.edit')}
                          >
                            <FiEdit2 />
                          </button>
                          <button
                            className="btn btn-danger btn-icon"
                            onClick={() => handleDelete(gv)}
                            title={t('common.delete')}
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal thêm/sửa */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={selectedGV ? t('gv.modalEditTitle') : t('gv.modalAddTitle')}
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setModalOpen(false)}>
              {t('common.cancel')}
            </button>
            <button className="btn btn-primary" onClick={handleSubmit}>
              {selectedGV ? t('common.update') : t('common.addNew')}
            </button>
          </>
        }
      >
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>{t('gv.fieldName')}</label>
            <input
              type="text"
              className="form-control"
              value={formData.tenGv}
              onChange={(e) => setFormData({ ...formData, tenGv: e.target.value })}
              placeholder={t('gv.fieldNamePlaceholder')}
              required
            />
          </div>
          <div className="form-group">
            <label>{t('gv.fieldSpecialty')}</label>
            <input
              type="text"
              className="form-control"
              value={formData.chuyenMon}
              onChange={(e) => setFormData({ ...formData, chuyenMon: e.target.value })}
              placeholder={t('gv.fieldSpecialtyPlaceholder')}
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>{t('gv.fieldEmail')}</label>
              <input
                type="email"
                className="form-control"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="email@example.com"
              />
            </div>
            <div className="form-group">
              <label>{t('gv.fieldPhone')}</label>
              <input
                type="text"
                className="form-control"
                value={formData.dienThoai}
                onChange={(e) => setFormData({ ...formData, dienThoai: e.target.value })}
                placeholder="0123456789"
              />
            </div>
          </div>
        </form>
      </Modal>

      {/* Confirm xóa */}
      <ConfirmDialog
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={confirmDelete}
        title={t('gv.confirmDeleteTitle')}
        message={t('gv.confirmDeleteMsg', { name: selectedGV?.ten_gv })}
      />

      {/* Import Excel */}
      <ImportExcel
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        onImport={handleImport}
        title={t('gv.importTitle')}
        templateColumns={['Tên giáo viên', 'Chuyên môn', 'Email', 'Điện thoại']}
        templateData={[
          ['Nguyễn Văn A', 'Toán', 'nguyenvana@email.com', '0901234567'],
          ['Trần Thị B', 'Văn', 'tranthib@email.com', '0912345678'],
          ['Lê Văn C', 'Anh', '', '']
        ]}
        templateFileName="mau_giao_vien.xlsx"
      />

      {/* Modal yêu cầu thời gian dạy GV */}
      <Modal
        isOpen={!!constraintGV}
        onClose={() => setConstraintGV(null)}
        title={constraintGV ? `Yêu cầu thời gian: ${constraintGV.ten_gv}` : ''}
      >
        {constraintGV && (
          <GvConstraintGrid
            gvId={constraintGV.gv_id}
            tenGv={constraintGV.ten_gv}
            onClose={() => setConstraintGV(null)}
          />
        )}
      </Modal>
    </div>
  );
};

export default GiaoVienPage;
